import cn from "classnames"
import { Fragment } from "react"

import { Card, Divider, Flex, Skeleton, Space, Text, Tooltip } from "@/atoms"
import { NAMES_ROUTES } from "@/const/routes.constants"
import { useTranslation } from "@/hooks/useTranslation"
import { DisconnectOutlined, VmIcons } from "@/icons"
import { ExternalLinkIcon } from "@/molecules/ExternalLinkIcon"
import { IconResource } from "@/molecules/Resources"

import styles from "./ResourceOverviewCard.module.scss"

import type { ResourceOverviewCardProps } from "."

export const ResourceOverviewCard = (props: ResourceOverviewCardProps) => {
  const { t } = useTranslation()
  const { t: tRelatedResources } = useTranslation("translation", {
    keyPrefix: "inventory.relatedResources"
  })
  const {
    images,
    vms,
    type,
    imageSource,
    isLoading,
    paramsExternalLink,
    title = tRelatedResources("image.title")
  } = props
  const imagesCount = images?.length ?? 0
  const isImageAvailable = !!imagesCount
  const isResourceAvailable = isImageAvailable || !!vms?.length
  const MAX_IMAGES_TO_DISPLAY = 3
  const shouldCutOffImages = imagesCount > MAX_IMAGES_TO_DISPLAY

  return (
    <Card
      title={title}
      classNames={{
        header: styles.header,
        body: cn(styles.body, {
          [styles.bodyNoResource]: !isResourceAvailable,
          [styles.bodyLoading]: isLoading
        })
      }}
    >
      {isLoading && (
        <>
          <span />
          <Skeleton active paragraph={{ rows: 1 }} />
        </>
      )}
      {!isLoading && (
        <>
          {isResourceAvailable && (
            <Flex vertical className={styles.minWidth0}>
              {vms?.map?.(
                ({ id, displayName, instanceId, subjectType }, index) => (
                  <Fragment key={id}>
                    {!!index && <Divider className={styles.divider} />}
                    <Flex
                      gap={4}
                      align="center"
                      className={cn(styles.minWidth0, styles.resourceWrapper)}
                    >
                      <VmIcons type={subjectType} className={styles.vmIcon} />
                      <Text className="sm-text-ks">
                        {displayName || instanceId}
                      </Text>
                    </Flex>
                  </Fragment>
                )
              )}
              {images?.map((image, index) => {
                if (shouldCutOffImages && index > MAX_IMAGES_TO_DISPLAY)
                  return null

                const displayImage =
                  !shouldCutOffImages || index < MAX_IMAGES_TO_DISPLAY
                const {
                  imageRepository = "",
                  name = imageRepository,
                  tag = "",
                  digest = ""
                } = image

                const isTag = !!tag
                const isDigest = !!digest
                const showTagOrDigest = isTag || isDigest
                const plus = imagesCount - MAX_IMAGES_TO_DISPLAY

                return (
                  <Fragment key={`${name}-${tag}-${digest}-${index}`}>
                    {(!!index || !!vms?.length) && (
                      <Divider className={styles.divider} />
                    )}
                    {displayImage && (
                      <Flex vertical gap={isTag || isDigest ? 4 : 0}>
                        <Flex
                          gap={6}
                          className={cn(
                            styles.minWidth0,
                            styles.resourceWrapper,
                            showTagOrDigest && styles.resourceWrapperTop
                          )}
                        >
                          {!!type && (
                            <IconResource
                              type={type}
                              imageSource={imageSource}
                            />
                          )}
                          <Tooltip title={<Text copyable>{name}</Text>}>
                            <Text ellipsis className="sm-text-ks">
                              {name}
                            </Text>
                          </Tooltip>
                        </Flex>
                        {showTagOrDigest && (
                          <Space
                            className={cn(
                              styles.minWidth0,
                              styles.resourceWrapper,
                              styles.resourceWrapperBottom
                            )}
                            separator={
                              <Text type="secondary" className="sm-text-ks">
                                |
                              </Text>
                            }
                          >
                            {isTag && (
                              <Text type="secondary" className="sm-text-ks">
                                {`${t("general.tag")}: ${tag}`}
                              </Text>
                            )}
                            {isDigest && (
                              <Text type="secondary" className="sm-text-ks">
                                {`${t("general.digest")}: ${digest}`}
                              </Text>
                            )}
                          </Space>
                        )}
                      </Flex>
                    )}
                    {!displayImage && index === MAX_IMAGES_TO_DISPLAY && (
                      <Flex
                        gap={4}
                        align="center"
                        className={cn(styles.minWidth0, styles.resourceWrapper)}
                      >
                        <ExternalLinkIcon
                          wrap
                          className={styles.imagesExternalLink}
                          iconPlacement="start"
                          text={t("general.seeAllImages")}
                          href={`/${NAMES_ROUTES.DISCOVERY.ROOT}/${NAMES_ROUTES.DISCOVERY.IMAGES}${paramsExternalLink ? `?${paramsExternalLink}` : ""}`}
                        />
                        <Text
                          type="secondary"
                          className="sm-text-ks"
                        >{`+${plus} ${t("general.image", { count: plus })}`}</Text>
                      </Flex>
                    )}
                  </Fragment>
                )
              })}
            </Flex>
          )}
          {!isResourceAvailable && (
            <Flex align="center" gap={4}>
              <span className={styles.iconNoCorrelation}>
                <DisconnectOutlined />
              </span>
              <Text className="sm-text-ks">
                {tRelatedResources("image.noLinked")}
              </Text>
            </Flex>
          )}
        </>
      )}
    </Card>
  )
}
